import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Onboard as OnboardService } from './onboard.svc';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';
import { BehaviorSubject } from 'rxjs';
import { SpinnerService, StrapiService, ToastService } from '@core/services';
import { FormInputComponent, ToastComponent } from '@core/components';
import { SvgIconComponent } from 'angular-svg-icon';
import { AsyncPipe } from '@angular/common';
import { PopoverDirective } from '@core/directives';

interface OnboardContent {
  UserInstruction: string;
  BusinessSegmentInstruction: string;
  FormContext: string;
  cards: {
    Nggp: string;
    NgpIntrastate: string;
    NotAny: string;
  };
}

@Component({
  selector: 'oke-onboard',
  imports: [
    AsyncPipe,
    FormInputComponent,
    RecaptchaV3Module,
    ToastComponent,
    SvgIconComponent,
    ReactiveFormsModule,
    PopoverDirective
  ],
  providers: [OnboardService],
  templateUrl: './onboard.html',
  styleUrl: './onboard.scss'
})
export class Onboard implements OnInit, OnDestroy {
  onboardForm: FormGroup;
  bodyElem = document.body;
  headElem = document.getElementById('app-header');
  reCAPTCHAToken = '';
  isSuccessfulFormSubmission = false;
  isLoading = false;
  svgIconSrc: Record<string, string> = {
    info: '/assets/svg/circle-info.svg'
  };
  selectedSegments: string[] = [];
  strapiContentSource = new BehaviorSubject<OnboardContent>({
    // eslint-disable-next-line max-len
    UserInstruction:
      'Please enter the following information for the user requesting access to ONEOK’s Customer Portal. Access is assigned at an individual level, so it is important that the email address entered below is registered to the individual user (no shared email accounts).',
    // eslint-disable-next-line max-len
    BusinessSegmentInstruction:
      'Please select the ONEOK Business Segment that you and/or your company is involved with by clicking on the segment cards below (Users typcially belong to one segment).',
    // eslint-disable-next-line max-len
    FormContext:
      'This request is for access to the ONEOK Customer Portal. The Nominations Portal is separate and requires that you first complete registration. Once you are granted access, you will be able to request access to the Nominations portal from within Customer Portal.',
    cards: {
      // eslint-disable-next-line max-len
      Nggp: '<p>Are you doing any <strong>Gathering &amp; Processing</strong> business with us? Customers identify as producers or shippers doing business with <strong>gathering pipelines or plants</strong> in the following areas:</p><ul><li>North Dakota</li><li>Montana</li><li>Wyoming</li><li>Kansas</li><li>Oklahoma</li></ul><p><strong>Apps used</strong>:  TIPS; ONEOK Customer Portal; EBB/OFS</p>',
      // eslint-disable-next-line max-len
      NgpIntrastate:
        '<p>Are you doing any <strong>Intrastate Pipeline</strong> business with us?</p><ul><li>ONEOK Gas Transportation</li><li>ONOK Gas Storage</li><li>ONEOK Western Trails Pipeline</li><li>ONEOK WesTex Transmission</li><li>ONEOK Texas Gas Storage</li><li>Roadrunner Gas Transmission</li><li>Mid Continent Market Center</li></ul><p><strong>Apps used</strong>:  QPTM/myQuorum; EBB; ONEOK Customer Portal</p>',
      // eslint-disable-next-line max-len
      NotAny:
        '<p>Are you unsure of which area of business at ONEOK that you are involved with? If yes, click this card.</p>'
    }
  });
  strapiContent$ = this.strapiContentSource.asObservable();
  segmentSelections: {
    segment: string;
    content: string;
  }[] = [];
  private readonly _fb = inject(FormBuilder);
  private readonly _onboardSvc = inject(OnboardService);
  readonly spinnerSvc = inject(SpinnerService);
  private readonly _toastSvc = inject(ToastService);
  private readonly _reCaptchaV3Svc = inject(ReCaptchaV3Service);
  private readonly _strapiSvc = inject(StrapiService);

  ngOnInit(): void {
    this.spinnerSvc.apiLoading$.subscribe(apiInfo => {
      this.isLoading = apiInfo.loading;
    });

    const recaptchaEl = document.getElementsByClassName('grecaptcha-badge')[0] as HTMLElement;
    this.getPageContent();
    this.initializeExternalView();
    this.intitializeForm();

    if (recaptchaEl) {
      recaptchaEl.style.visibility = 'visible';
    }
  }

  ngOnDestroy(): void {
    const recaptchaEl = document.getElementsByClassName('grecaptcha-badge')[0] as HTMLElement;
    this.restoreLoginPageView();
    recaptchaEl.style.visibility = 'hidden';
  }

  getPageContent(): void {
    const strapiPageOnboard = JSON.parse(sessionStorage.getItem('strapiPageOnboard') as string);
    if (!strapiPageOnboard) {
      this._strapiSvc.getOnboardContent().subscribe(
        contentFields => {
          sessionStorage.setItem('strapiPageOnboard', JSON.stringify(contentFields));

          this.buildSegmentSelections(contentFields);
          this.strapiContentSource.next(contentFields);
        },
        () => undefined
      );
    } else {
      // Rebuild the business segment cards from cached content
      this.buildSegmentSelections(strapiPageOnboard);
      this.strapiContentSource.next(strapiPageOnboard);
    }
  }

  buildSegmentSelections(contentFields: OnboardContent): void {
    if (!contentFields || !contentFields.cards) {
      return;
    }
    this.segmentSelections = [];
    for (const [prop, content] of Object.entries(contentFields.cards)) {
      const theSegment: { segment: string; content: string } = {
        content,
        segment: ''
      };
      if (prop === 'NotAny') {
        theSegment.segment = 'N/A';
      } else if (prop === 'Nggp') {
        theSegment.segment = 'NGGP';
      } else if (prop === 'NgpIntrastate') {
        theSegment.segment = 'NGPIntrastate';
      }
      this.segmentSelections.push(theSegment);
    }
  }

  intitializeForm(): void {
    const emailRegEx =
      /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
    const mobileRegex = /^(\+\d{1,3}[- ]?)?\d{10}$/;
    this.onboardForm = this._fb.group({
      companyName: ['', Validators.required],
      street1: [''],
      street2: [''],
      city: [''],
      state: [''],
      postalCode: [''],
      country: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.pattern(emailRegEx)]],
      mobile: ['', [Validators.required, Validators.pattern(mobileRegex)]],
      oneokContact: ['', Validators.required],
      additionalComments: [''],
      businessSegments: ['']
    });
  }

  submitOnboardFormToCreateOnboardRequest(): void {
    this.spinnerSvc.showSpinnerButton();
    this._toastSvc.dismissAll();
    //recapthcaV3 validation
    this._reCaptchaV3Svc.execute('importantAction').subscribe(token => {
      //if recaptchaV3 validation is passed process the onboard request
      if (token) {
        this.reCAPTCHAToken = token;
        this._onboardSvc.createOnboardRequest(this.onboardForm.value).subscribe(
          () => {
            this.isSuccessfulFormSubmission = true;
            this.spinnerSvc.hideSpinnerButton();
            this.intitializeForm();
            this.resetBusinessSegments();
            document?.getElementById('app-main')?.scrollTo(0, 0);
          },
          () => {
            this._toastSvc.dismissAll();
            this.spinnerSvc.hideSpinnerButton();
            document?.getElementById('app-main')?.scrollTo(0, 0);
            setTimeout(() => {
              this._toastSvc.alert(
                'Access Request',
                "We're sorry &ndash; something went wrong processing your request."
              );
            }, 1000);
          }
        );
      }
    });
  }

  businessSegmentSelect(event: Event): void {
    const currSegment = (event.currentTarget as HTMLElement).id;
    const currSegmentCard = document.getElementById(currSegment) as HTMLElement;
    //if user unchecked a checked business segment then reset the state of the selected segments
    if (currSegmentCard.classList.contains('active-segment')) {
      currSegmentCard.classList.remove('active-segment');
      const filteredSegmentArr = this.selectedSegments.filter(segment => segment !== currSegment);
      this.selectedSegments = filteredSegmentArr;
    } else {
      currSegmentCard.classList.add('active-segment');
      this.selectedSegments.push(currSegment);
      this.onboardForm.controls.businessSegments.setValue(this.selectedSegments);
    }
  }

  resetBusinessSegments(): void {
    document.querySelectorAll('.segment-card.active-segment').forEach(card => card.classList.remove('active-segment'));
    this.selectedSegments = [];
    this.onboardForm.controls.businessSegments.setValue('');
  }

  //Remove internal styling from body of app (Note: Should be modified to using NG Class for conditional styling)
  initializeExternalView(): void {
    this.bodyElem.className = 'is-unauthenticated is-onboard';
  }

  //Apply internal styling to body of app (Note: Should be modified to using NG Class for conditional styling)
  initializeInternalView(): void {
    this.bodyElem.classList.remove('is-unauthenticated', 'is-onboard');
  }

  restoreLoginPageView(): void {
    this.bodyElem.classList.add('is-unauthenticated');
    this.bodyElem.classList.remove('is-onboard');
  }
}
